// ---------------------------------------------------------
// Datei: api.extensions.ts
// Zweck: Erweiterungen für API-Service (Upload-Funktionen)
// Besonderheiten:
// - Verbindet Frontend-Upload mit Backend-Route
import { getAuthApiBaseUrl, getGoogleAuthHeaders } from "./googleAuthSession";
// - Konvertiert Base64-Bilder zu Blob für Multipart-Upload
// - Backend-Route leitet an HubSpot weiter
// Stand: 28. November 2025
// ---------------------------------------------------------

import api from "./api";

/**
 * Upload-Response vom Backend (HubSpot-Endpunkt)
 */
interface UploadResponse {
  success: boolean;
  fileId: string;
  dealId: string;
  property: string;
  warning?: string;
}



/**
 * Bild hochladen zu HubSpot (via Backend)
 *
 * Ablauf:
 * 1. Base64-String wird zu Blob konvertiert
 * 2. Blob wird als FormData an Backend gesendet
 * 3. Backend lädt zu HubSpot hoch und patcht Deal
 * 4. FileId wird zurückgegeben für spätere Ansicht
 *
 * @param phaseId - Format: "{dealId}-{phaseName}" (z.B. "313739230424-vorab")
 * @param checklistItemId - ID aus document-types.json (z.B. "typenschild-altanlage")
 * @param imageData - Base64-String des Bildes
 * @param fileName - Dateiname für Upload
 * @returns FileId der hochgeladenen Datei (für View-Funktion)
 */
export const uploadImage = async (
  phaseId: string,
  checklistItemId: string,
  imageData: string,
  fileName: string
): Promise<string | void> => {
  // FormData erstellen
  const formData = new FormData();

  // Base64-String in Blob umwandeln
  const base64Response = await fetch(imageData);
  const blob = await base64Response.blob();

  // Blob zum FormData hinzufügen
  formData.append("image", blob, fileName);

  // Bild hochladen (Backend leitet an HubSpot weiter)
  const response = await api.post<UploadResponse>(
    `/phases/${phaseId}/checklist/${checklistItemId}/image`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  // FileId für spätere Ansicht zurückgeben
  console.log(
    `[Upload] Success: fileId=${response.fileId}, dealId=${response.dealId}, property=${response.property}`
  );

  return response.fileId;
};

/**
 * Dokument löschen (Property im Deal leeren)
 *
 * @param phaseId - Format: "{dealId}-{phaseName}"
 * @param checklistItemId - ID aus document-types.json
 */
export const deleteDocument = async (
  phaseId: string,
  checklistItemId: string
): Promise<void> => {
  // Property-Name mappen (muss mit Backend übereinstimmen)
  // Wir nutzen hier eine vereinfachte Logik, idealerweise sollte das Mapping zentral sein
  // Aber da das Backend das Mapping macht, rufen wir einen speziellen Endpoint auf
  // ODER wir nutzen den existierenden /phases Endpoint mit DELETE?
  // Nein, wir nutzen den neuen /api/hubspot/deal/clear Endpoint, aber wir müssen den Property-Namen wissen.
  // Das ist schwierig, da das Mapping im Backend (hubspotDealMapper) oder Frontend (hubspotDealMapper) liegt.
  // Besser: Wir nutzen einen neuen Endpoint im phases.express.ts, der das Mapping übernimmt.

  // Workaround: Wir rufen den phases-Endpoint mit DELETE auf.
  // Aber phases.express.ts hat noch kein DELETE.
  // Wir fügen es dort hinzu.
  await api.delete(`/phases/${phaseId}/checklist/${checklistItemId}`);
};

/**
 * Datei zu Google Drive hochladen (via Backend /api/drive/)
 *
 * @param dealName   - Deal-Name für Drive-Ordnerstruktur (z.B. "Wärmepumpeninstallation Müller")
 * @param imageData  - Base64-Data-URL des Bildes
 * @param fileName   - Dateiname
 * @param directories - Optionale Unterordner (z.B. ["Vorbereitung", "heizlastberechnung"])
 * @returns fileId der hochgeladenen Datei oder null bei Fehler
 */
export const uploadToDrive = async (
  dealName: string,
  imageData: string,
  fileName: string,
  directories?: string[],
  sharedDrive?: string,
  gdriveFolderId?: string,
): Promise<{ fileId: string; webViewLink: string; thumbnailLink?: string } | null> => {
  const AUTH_BASE = getAuthApiBaseUrl();
  const authHeaders = await getGoogleAuthHeaders();

  // Base64 → Blob → File (einmalig erzeugen)
  const base64Response = await fetch(imageData);
  const blob = await base64Response.blob();
  const file = new File([blob], fileName, { type: blob.type });

  // Neuer Endpoint wenn HubSpot-Folder-ID vorhanden – kein Fallback auf alten Endpoint,
  // da Upload bereits erfolgreich in Drive gelandet sein könnte (nicht idempotent).
  if (gdriveFolderId) {
    const newFormData = new FormData();
    newFormData.append("files", file);
    newFormData.append("baseFolderId", gdriveFolderId);
    if (directories?.length) {
      newFormData.append("directories", JSON.stringify(directories));
    }

    const newResponse = await fetch(`${AUTH_BASE}/google/drive/upload/new`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders,
      body: newFormData,
    });

    if (!newResponse.ok) {
      if (newResponse.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      const text = await newResponse.text();
      throw new Error(`Drive Upload fehlgeschlagen: ${newResponse.status} - ${text}`);
    }

    const newJson = await newResponse.json();
    if (!newJson.status) {
      if (/403|session denied/i.test(newJson.message || '')) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      throw new Error(newJson.message || "Drive Upload fehlgeschlagen");
    }

    const newFile = newJson.files?.[0] ?? null;
    if (!newFile?.fileId) return null;
    return {
      fileId: newFile.fileId,
      webViewLink: newFile.webViewLink || `https://drive.google.com/file/d/${newFile.fileId}`,
      thumbnailLink: newFile.thumbnailLink,
    };
  }

  // Alter Endpoint – nur wenn keine gdriveFolderId
  const formData = new FormData();
  formData.append("files", file);
  formData.append("dealName", dealName);

  if (directories?.length) {
    formData.append("directories", JSON.stringify(directories));
  }

  if (sharedDrive) {
    formData.append("sharedDrive", sharedDrive);
  }

  const response = await fetch(`${AUTH_BASE}/google/drive/upload`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders,
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 403) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    const text = await response.text();
    throw new Error(`Drive Upload fehlgeschlagen: ${response.status} - ${text}`);
  }

  const json = await response.json();

  if (!json.status) {
    if (/403|session denied/i.test(json.message || '')) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    throw new Error(json.message || "Drive Upload fehlgeschlagen");
  }

  const driveFile = json.files?.[0] ?? null;

  if (!driveFile?.fileId) return null;
  return {
    fileId: driveFile.fileId,
    webViewLink: driveFile.webViewLink || `https://drive.google.com/file/d/${driveFile.fileId}`,
    thumbnailLink: driveFile.thumbnailLink,
  };
};

// Weitere API-Erweiterungen hier hinzufügen

export const deleteDriveFile = async (fileId: string): Promise<void> => {
  const authBase = getAuthApiBaseUrl();
  const authHeaders = await getGoogleAuthHeaders();
  const res = await fetch(`${authBase}/google/drive/file/${fileId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: authHeaders,
  });
  const json = await res.json();
  if (!res.ok || !json.status) {
    throw new Error(`Drive Delete fehlgeschlagen: ${json.message || res.status}`);
  }
};

export default {
  uploadImage,
  uploadToDrive,
  deleteDocument,
  deleteDriveFile,
};
